import * as React from "react";
import { useState, useEffect } from "react";
import Image from "next/image";
import { shimmer, toBase64 } from "@src/utils/shimmer";
import { motion, AnimatePresence } from "framer-motion";
import Link from "next/link";
import { GetAlbumDetailsQuery } from "@src/@types/api.d";

type Props = {
  eventId: number;
  albumDetailsData: GetAlbumDetailsQuery;
  eventSlug: string;
};

const Masonry: React.FC<Props> = ({ eventId, eventSlug, albumDetailsData }) => {
  const mediaData = React.useMemo(
    () => albumDetailsData?.getMedia || [],
    [albumDetailsData]
  );
  const [currentIndex, setCurrentIndex] = useState(0);

  const isValidImageUrl = (img: any): img is { meta: { s3_url: string } } => {
    return (
      img &&
      img.meta &&
      typeof img.meta.s3_url === "string" &&
      img.meta.s3_url.trim() !== "" &&
      img.meta.s3_url.startsWith("http") &&
      img.type?.toLowerCase().includes("image") // Added type check for images only
    );
  };

  const validImages = React.useMemo(() => {
    return mediaData.filter(isValidImageUrl);
  }, [mediaData]);

  // Only show first 3 images in the stack
  const stackImages = React.useMemo(() => {
    return validImages.slice(0, 3);
  }, [validImages]);

  useEffect(() => {
    if (stackImages.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % stackImages.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [stackImages.length]);

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % stackImages.length);
  };

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? stackImages.length - 1 : prev - 1));
  };

  if (!stackImages.length) {
    return null;
  }

  // Stack configuration for layering effect
  const getStackProps = (
    index: number
  ): {
    scale: number;
    rotate: number;
    y: number;
    x: number;
    zIndex: number;
    opacity: number;
    filter: string;
  } => {
    const adjustedIndex =
      (index - currentIndex + stackImages.length) % stackImages.length;

    const configs = [
      // Top card (active)
      {
        scale: 1,
        rotate: 0,
        y: 0,
        x: 0,
        zIndex: 50,
        opacity: 1,
        filter: "brightness(1)",
      },
      // Second card
      {
        scale: 0.95,
        rotate: -5,
        y: 8,
        x: -12,
        zIndex: 40,
        opacity: 0.9,
        filter: "brightness(0.95)",
      },
      // Third card
      {
        scale: 0.9,
        rotate: 5,
        y: 16,
        x: 8,
        zIndex: 30,
        opacity: 0.8,
        filter: "brightness(0.9)",
      },
    ];

    return configs[adjustedIndex] || configs[configs.length - 1];
  };

  return (
    <div className="bg-[#FEFEFE] h-[85vh] md:h-[120vh] flex flex-col items-center justify-start relative z-[10] px-4 py-12">
      <div className="max-w-4xl mx-auto w-full">
        {/* Stacked Images Container */}
        <motion.div
          variants={{
            hidden: { opacity: 0, y: -30 },
            visible: {
              opacity: 1,
              y: 0,
              transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] },
            },
          }}
          className="text-center mb-8"
        >
          <h3
            className={`font-dacing font-semibold lg:font-normal text-[#4A2C47] text-4xl lg:text-6xl mb-4 tracking-wide`}
          >
            Through the Lens of Love
          </h3>
          <motion.div
            initial={{ width: 0 }}
            whileInView={{ width: "6rem" }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className={`h-1 mx-auto rounded-full bg-[#4A2C47]`}
          ></motion.div>
        </motion.div>
        <div className="relative w-full h-[50vh] md:h-[70vh] flex items-center justify-center mt-2">
          <div className="relative w-full max-w-sm mx-auto md:max-w-lg lg:max-w-xl h-[400px] md:h-[600px]">
            {stackImages.map((image, index) => {
              const stackProps = getStackProps(index);

              return (
                <motion.div
                  key={`${image?.meta?.s3_url}-${index}`}
                  className="absolute inset-0 rounded-3xl overflow-hidden cursor-pointer"
                  style={{
                    zIndex: stackProps.zIndex,
                    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                  }}
                  animate={{
                    scale: stackProps.scale,
                    rotate: stackProps.rotate,
                    y: stackProps.y,
                    x: stackProps.x,
                    opacity: stackProps.opacity,
                  }}
                  transition={{
                    duration: 0.6,
                    ease: [0.4, 0, 0.2, 1],
                  }}
                  onClick={stackProps.zIndex === 50 ? handleNext : () => {}}
                >
                  <div className="relative w-full h-full">
                    <div
                      style={
                        stackProps.filter ? { filter: stackProps.filter } : {}
                      }
                      className="w-full h-full"
                    >
                      <Image
                        placeholder="blur"
                        blurDataURL={`data:image/svg+xml;base64,${toBase64(
                          shimmer(700, 475)
                        )}`}
                        src={image?.meta?.s3_url ?? ""}
                        layout="fill"
                        alt={`Gallery image ${index + 1}`}
                        objectFit="cover"
                        className="object-center"
                        onError={(
                          e: React.SyntheticEvent<HTMLImageElement, Event>
                        ) => {
                          console.error(
                            "Image load error:",
                            image?.meta?.s3_url
                          );
                        }}
                      />
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" />
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Navigation Buttons */}
          {stackImages.length > 1 && (
            <>
              <button
                onClick={handlePrevious}
                className="absolute left-4 z-[60] w-12 h-12 bg-[FEFEFE]/80 hover:bg-[#FEFEFE] rounded-full flex items-center justify-center shadow-lg transition-all duration-200 hover:scale-110 backdrop-blur-sm"
                aria-label="Previous image"
              >
                <svg
                  className="w-6 h-6 text-[#4A2C47]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
              <button
                onClick={handleNext}
                className="absolute right-4 z-[60] w-12 h-12 bg-[#FEFEFE]/80 hover:bg-[#FEFEFE] rounded-full flex items-center justify-center shadow-lg transition-all duration-200 hover:scale-110 backdrop-blur-sm"
                aria-label="Next image"
              >
                <svg
                  className="w-6 h-6 text-[#4A2C47]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </>
          )}
        </div>

        {/* Button */}
        {eventSlug && (
          <div className="flex justify-center">
            <Link
              href={`https://${eventSlug}.${process.env.NEXT_PUBLIC_INAWO_ALBUM_SITE_END}`}
              passHref
            >
              <a target="_blank" rel="noopener noreferrer">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-3 rounded-full transition-all duration-300 border-2 border-[#4A2C47] text-[#4A2C47] text-xl font-corGaramond font-bold hover:bg-[#4A2C47] hover:text-[#FEFEFE] shadow-lg hover:shadow-xl backdrop-blur-sm mt-4"
                  type="button"
                >
                  See Full Photobook
                </motion.button>
              </a>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default Masonry;
